// This file configures the initialization of Sentry on the browser.
// The config you add here will be used whenever a page is visited.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRewritesTunnelPath"] = undefined;globalThis["SENTRY_RELEASE"] = undefined;globalThis["_sentryBasePath"] = undefined;globalThis["_sentryNextJsVersion"] = "15.5.16";globalThis["_sentryRewriteFramesAssetPrefixPath"] = "";globalThis["_sentryAssetPrefix"] = undefined;globalThis["_sentryExperimentalThirdPartyOriginStackFrames"] = undefined;globalThis["_sentryRouteManifest"] = "{\"isrRoutes\":[],\"dynamicRoutes\":[],\"staticRoutes\":[]}";import * as Sentry from '@sentry/nextjs';

// Noise errors that dominate quota but carry zero signal. Every entry below was
// verified against 14 days of live Sentry data by reading the actual stack of
// the top issues — not by guessing from the title.
//
// NOTE: two things were deliberately REMOVED from this list.
//   - 'NotAllowedError' / 'The request is not allowed by the user agent':
//     these were NOT a blocked platform API. All 523k events (6,051 users) came
//     from one place — navigator.clipboard.writeText() in the share-ticket flow,
//     firing after the request resolved, once Safari's user-gesture window had
//     already closed. That is a real bug (fixed in use-download-ticket.tsx), and
//     filtering it here would have hidden it forever.
//   - 'attempted to hard navigate to the same URL' / 'Failed to load static
//     props': these are the symptom of a chunk 404 after a stale deploy. Real
//     signal, so they are sampled in beforeSend rather than dropped outright.
const IGNORE_ERRORS = [
  // Next.js router internals — the user navigated away mid-transition.
  // ('routeChange aborted' alone was 2.6M events / 42% of the entire bill.)
  'routeChange aborted',
  'Route Cancelled',
  'Loading initial props cancelled',
  'Non-Error promise rejection captured',
  // Third-party SDKs that never loaded (adblocked, or the tag is disabled).
  // A config problem, not a code bug — tracked outside Sentry.
  "Can't find variable: Adjust",
  'Adjust is not defined',
  "Can't find variable: braze",
  'braze is not defined',
  // AppDynamics' own agent script probing APIs that don't exist in the browser
  "Can't find variable: HTMLEmbedElement",
  // Other apps' in-app browsers (Facebook/Instagram/Snapchat) and extensions:
  // their native bridge, not our page.
  'Java object is gone',
  'Java bridge method invocation error',
  'Java exception was raised',
  'SCDynimacBridge',
  'webkit.messageHandlers',
  'WKWebView API client did not respond',
  'runtime.sendMessage',
  "Can't find variable: CONFIG",
  // Google Maps' own loader
  'Could not load "util"',
  'Could not load "onion"',
  'Blocked a frame with origin',
  // Browser/network conditions we cannot act on
  "Failed to execute 'transaction' on 'IDBDatabase'",
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Failed to fetch',
  'NetworkError when attempting to fetch resource',
  'Load failed',
  'AbortError',
  'ChunkLoadError',
  'Loading chunk',
  'Loading CSS chunk',
];

// Symptoms of a chunk 404 after a stale deploy. Real, but self-healing (_app.tsx
// hard-reloads once), and ~684k events/14d. Keep a 1% sample so a broken deploy
// is still visible without paying for the flood.
const SAMPLED_ERRORS = [
  'attempted to hard navigate to the same URL',
  'Failed to load static props',
];
const SAMPLED_ERROR_RATE = 0.01;

Sentry.init({
  dsn: 'https://19261e9b075c4b059dd45b6c4d336eaf@o1326668.ingest.sentry.io/4504683779522560',
  // Disable ONLY in local dev (`next dev`). Every deployed build sends,
  // regardless of its NODE_ENV value (this app uses non-standard values
  // like `prod`/`uat`/`staging`, so gating on ==='production' would wrongly
  // silence real prod).
  enabled: process.env.NODE_ENV !== 'development',
  environment: process.env.NODE_ENV,

  // Was 1.0 (100%). At ~256M spans/30d that blew the quota. 1% keeps a
  // statistical sample for Performance dashboards at ~1% of the cost.
  tracesSampleRate: 0.01,

  ignoreErrors: IGNORE_ERRORS,
  denyUrls: [
    /extensions\//i,
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /^safari-extension:\/\//,
    /googletagmanager\.com/,
    /google-analytics\.com/,
    /connect\.facebook\.net/,
    // Errors thrown inside third-party scripts we merely embed. Matched on the
    // path because Sentry rewrites these frames to `app:///<path>`.
    /adrum/i, // AppDynamics agent — 1.09M events/14d, all from its own script
    /apple-pay-sdk/i, // Apple's Apple Pay SDK failing to parse in old browsers
    /maps\/api\/js/i, // Google Maps loader
    /cdn\.adjust\.com/i,
    /js\.appboycdn\.com/i, // Braze
    /cdn\.onesignal\.com/i,
  ],

  beforeSend(event, hint) {
    const name = hint?.originalException?.name;
    if (name === 'AbortError' || name === 'ChunkLoadError') return null;

    // Empty shells: no type, no message, nothing to act on (e.g. "Error: 0",
    // "<unknown>"). ~40k events/14d and not one of them is debuggable.
    const value = event.exception?.values?.[0];
    const message = value?.value ?? event.message ?? '';
    if (!message || message === '0' || message === '<unknown>') return null;

    // Keep only a small sample of the stale-deploy chunk errors.
    if (SAMPLED_ERRORS.some(pattern => message.includes(pattern))) {
      return Math.random() < SAMPLED_ERROR_RATE ? event : null;
    }

    return event;
  },
});
